import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { FileDownloadService } from '../../../core/services/ui/file-download.service';
import {
  DownloadFn,
  ReportFormat,
} from '../../../core/types/api/export-files.types';
import {
  DOWNLOAD_DELAYS,
  EXPORT_REPORT_I18N,
  FILE_NAME_PREFIX,
  PROGRESS_ANIMATION,
} from '../../data/export-reports/constants';

@Component({
  selector: 'shared-export-report-button',
  templateUrl: './export-report-button.component.html',
  styleUrls: ['./export-report-button.component.scss'],
})
export class ExportReportButtonComponent implements OnDestroy {
  @Input() downloadFn!: DownloadFn;
  @Input() filename: string = FILE_NAME_PREFIX;
  @Input() hasFilters: boolean = false;

  public progress: number = PROGRESS_ANIMATION.INITIAL;
  public isAll = false;
  public isDone = false;
  public isOpen = false;
  public isError = false;
  public isDownloading = false;

  private _sub: Subscription = new Subscription();
  private _progressTimer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private readonly _elementRef: ElementRef,
    private readonly _fileDownloadService: FileDownloadService,
    private readonly _translate: TranslateService,
  ) {}

  ngOnDestroy(): void {
    this._sub.unsubscribe();
    this._clearTimer();
  }

  public get scopeSuffix(): string {
    if (this.isAll)
      return this.hasFilters
        ? this._translate.instant(EXPORT_REPORT_I18N.SCOPE_FILTERED)
        : this._translate.instant(EXPORT_REPORT_I18N.SCOPE_ALL);
    return this._translate.instant(EXPORT_REPORT_I18N.SCOPE_PAGE);
  }

  public get buttonTooltip(): string {
    if (this.isDownloading)
      return this._translate.instant(EXPORT_REPORT_I18N.TOOLTIP_GENERATING, {
        progress: Math.round(this.progress),
      });
    if (this.isDone)
      return this._translate.instant(EXPORT_REPORT_I18N.TOOLTIP_DOWNLOADED);
    if (this.isError)
      return this._translate.instant(EXPORT_REPORT_I18N.TOOLTIP_ERROR);
    return this.isOpen
      ? ''
      : this._translate.instant(EXPORT_REPORT_I18N.TOOLTIP_EXPORT);
  }

  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: MouseEvent): void {
    if (this.isDownloading || this.isDone || this.isError) return;
    if (!this._elementRef.nativeElement.contains(event.target as Node))
      this.isOpen = false;
  }

  public togglePanel(): void {
    if (this.isDownloading || this.isDone || this.isError) return;
    this.isOpen = !this.isOpen;
  }

  public downloadFormat(format: ReportFormat): void {
    this.isOpen = false;
    this.isDownloading = true;
    this.isDone = false;
    this.isError = false;
    this.progress = PROGRESS_ANIMATION.INITIAL;
    this._animateProgress();

    this._sub.add(
      this.downloadFn(format, this.isAll).subscribe({
        next: (blob) => {
          this._clearTimer();
          this.progress = PROGRESS_ANIMATION.COMPLETED;
          this._fileDownloadService.downloadBlob(blob, this.filename, format);
          setTimeout(() => {
            this.isDownloading = false;
            this.isDone = true;
            setTimeout(() => {
              this.isDone = false;
              this.progress = PROGRESS_ANIMATION.INITIAL;
            }, DOWNLOAD_DELAYS.DONE_RESET_MS);
          }, DOWNLOAD_DELAYS.DONE_FINALIZE_MS);
        },
        error: () => {
          this._clearTimer();
          this.isDownloading = false;
          this.isError = true;
          this.progress = PROGRESS_ANIMATION.INITIAL;
          setTimeout(
            () => (this.isError = false),
            DOWNLOAD_DELAYS.ERROR_RESET_MS,
          );
        },
      }),
    );
  }

  private _animateProgress(): void {
    this._progressTimer = setInterval(() => {
      if (this.progress < PROGRESS_ANIMATION.MAX_ANIMATED) {
        const rate =
          this.progress < PROGRESS_ANIMATION.RATE_THRESHOLD
            ? (PROGRESS_ANIMATION.RATE_THRESHOLD - this.progress) *
                PROGRESS_ANIMATION.RATE_FAST_MULTIPLIER +
              PROGRESS_ANIMATION.RATE_FAST_BASE
            : (PROGRESS_ANIMATION.MAX_ANIMATED - this.progress) *
                PROGRESS_ANIMATION.RATE_SLOW_MULTIPLIER +
              PROGRESS_ANIMATION.RATE_SLOW_BASE;
        this.progress = Math.min(
          PROGRESS_ANIMATION.MAX_ANIMATED,
          this.progress + rate,
        );
      }
    }, PROGRESS_ANIMATION.INTERVAL_MS);
  }

  private _clearTimer(): void {
    if (this._progressTimer !== null) {
      clearInterval(this._progressTimer);
      this._progressTimer = null;
    }
  }
}
